import React, { useMemo } from 'react';
import { css } from "@emotion/react";

import Modal from "./modal/Modal";
import Button, { Color } from "./Button";
import theme from "../../theme";
import { getIsCountryMatchingPlanCountry, getPlanStoreNameFromCode } from "../includes/stripe";

type Props = {
    onClose: () => void
    onConfirm?: (countryCode: string) => void,
    cardCountryCode?: string,
    stripeLoaderCountryCode?: string,
    selectedCountryCode?: string,
}

const ACTIONS = {
    "got-it": {
        label: "Got it",
        isCancel: true,
        type: 'primary'
    },
    "cancel": {
        label: "Cancel",
        isCancel: true,
        type: 'red'
    },
    "confirm": {
        label: "Confirm",
        isCancel: false,
        type: 'primary'
    }
}

const CountryMismatchModal = (
    {
        onClose,
        onConfirm,
        cardCountryCode = "",
        stripeLoaderCountryCode = "",
        selectedCountryCode = "",
    }: Props
) => {
    cardCountryCode = cardCountryCode.toUpperCase();
    stripeLoaderCountryCode = stripeLoaderCountryCode.toUpperCase();
    selectedCountryCode = selectedCountryCode.toUpperCase();

    const [message, actionKeys] = useMemo(() => {
        let message = <div>
            Oops! &nbsp;Your card's country, location, and selected country don't match.
            <br />
            <br />
            Please check your details and try again.
        </div>;
        let actionKeys = ['got-it'];

        if (!cardCountryCode || !stripeLoaderCountryCode || !selectedCountryCode) return [message, actionKeys]

        const isCardCountryMatchingPlanCountry = getIsCountryMatchingPlanCountry(
            cardCountryCode,
            stripeLoaderCountryCode,
        );

        const isSelectedCountryMatchingPlanCountry = getIsCountryMatchingPlanCountry(
            selectedCountryCode,
            stripeLoaderCountryCode,
        );

        if (isCardCountryMatchingPlanCountry && cardCountryCode !== selectedCountryCode) {
            message = <div>
                Oops! &nbsp;Your payment card doesn't match your country. &nbsp;To continue, you'll need to try a
                different
                card or change your address.
            </div>;
        } else if ((cardCountryCode === selectedCountryCode && !isCardCountryMatchingPlanCountry) || (
            isSelectedCountryMatchingPlanCountry && !isCardCountryMatchingPlanCountry
        )) {
            let storeName = getPlanStoreNameFromCode(cardCountryCode);

            if (storeName?.toLowerCase() === "global") {
                storeName = `${storeName?.toLowerCase()} store (United States)`;
            } else {
                storeName += " store";
            }

            message = <div>
                Oops! &nbsp;Your payment card doesn't match your current location.
                <br />
                <br />
                To use this card, we need to update your location to the {storeName}. &nbsp;Heads
                up&mdash;this
                will affect the subscription price and currency.
                <br />
                <br />
                Hit 'Confirm' and we'll update your location, or click 'Cancel' to try a different card.
            </div>;
            actionKeys = ['cancel', 'confirm']
        }

        return [message, actionKeys]
    }, [cardCountryCode, selectedCountryCode, stripeLoaderCountryCode]);

    const onHandle = (isCancel: boolean) => {
        if (isCancel) {
            return onClose();
        } else if (onConfirm) {
            return onConfirm(cardCountryCode)
        }
    }

    const modalStyle = css`
        ${theme.breakpoints.down('xs')} {
            max-width: 90%;
        }
    `;

    const containerStyle = css`
        position: relative;
        width: 420px;

        ${theme.breakpoints.down('xs')} {
            width: 100%;
        }
    `;

    const contentContainerStyle = css`
        padding: 40px;
    `;

    const messageStyle = css`
        text-align: center;
        margin-bottom: 20px;
    `;

    const buttonsContainerStyle = css`
        width: 100%;
        display: flex;
        justify-content: space-evenly;

        ${theme.breakpoints.down('xxs')} {
            flex-direction: column;
            gap: 10px;
        }
    `;

    const buttonStyle = css`
        border-radius: ${theme.borderAndShadow.largerRadius1};
    `;

    return (
        <Modal>
            <Modal.DefaultContainer css={modalStyle}>
                <div css={containerStyle}>
                    <div css={contentContainerStyle}>
                        <div className={'modal-body'}>
                            <div css={messageStyle}>{message}</div>
                            <div css={buttonsContainerStyle}>
                                {actionKeys.map((key: string, index: number) => {
                                    const action = ACTIONS[key as keyof typeof ACTIONS];
                                    const colour = action.type as Color
                                    return (
                                        <Button
                                            key={index}
                                            onClick={() => onHandle(action.isCancel)}
                                            color={colour}
                                            css={buttonStyle}
                                        >
                                            {action.label}
                                        </Button>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.DefaultContainer>
        </Modal>
    )
}

export default CountryMismatchModal;